import * as React from "react";
import { Layout } from "../../layout";
import { request } from "../../services/cpln";
import { Alert, Button, Dropdown, Input, notification, Select } from "antd";
import { Loader, Search } from "react-feather";
import { getAuditTrailPresetsNew, getDefaultAuditTrailTimeNew, resolveSubjectName } from "./utils";
import { Audit } from "./types";
import { ViewModal } from "../../components/modals/viewModal";
import { AuditGridWrapper } from "../../components/detail/auditGridWrapper";
import { ScrollParams } from "react-virtualized";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import jsYaml from "js-yaml";
import { DownloadOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { selectDefaultValue, selectLabel, selectValue, setValue as setTimezoneValue } from "../../store/timezoneSlice";
import { getDefaultKindSelectState, KindSelect } from "../../components/antd/kindselect/KindSelect";
import {
  DateRangePicker,
  resolveExpressionPair,
  WithTimezone,
  parseDateRangeFromQueryObject,
  buildDateRangeQueryObject,
  timezoneToUrlValue,
  urlValueToTimezone,
} from "../../newcomponents/datepicker";

export const AuditTrail: React.FC = () => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);
  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);
  const localTimezone = useAppSelector(selectDefaultValue);

  const navigate = useNavigate();
  const location = useLocation();
  const [audits, setAudits] = React.useState<Audit[]>([]);

  // Parse URL params including timezone
  const queryObject = React.useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    // Only parse on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const { kind, name, subject, audit, id } = queryObject;

  // Parse date range and timezone from URL using utilities
  const urlDateParams = React.useMemo(
    () =>
      parseDateRangeFromQueryObject(queryObject, {
        defaultFrom: null,
        defaultTo: null,
        defaultTimezone: timezoneToUrlValue(timezoneValue, localTimezone),
      }),
    // Only compute on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const urlFrom = urlDateParams.from;
  const urlTo = urlDateParams.to;
  const urlTimezone = urlDateParams.timezone;
  const [selectedKind, setSelectedKind] = React.useState("any");
  const [nameInput, setNameInput] = React.useState("");
  const [idInput, setIdInput] = React.useState("");
  const [subjectInput, setSubjectInput] = React.useState("");

  const defaultAuditContextState = getDefaultKindSelectState({
    value: "cpln",
    link: `/org/${org}/auditctx/cpln`,
  });
  const [auditContextValue, setAuditContextValue] = React.useState(defaultAuditContextState.value);
  const [auditContextState, setAuditContextState] = React.useState(defaultAuditContextState);

  const [fromNew, setFromNew] = React.useState<string | null>(null);
  const [toNew, setToNew] = React.useState<string | null>(null);

  const [isUrlStateProcessed, setIsUrlStateProcessed] = React.useState(false);
  const [queried, setQueried] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showDetailsOf, setShowDetailsOf] = React.useState<null | string>(null);
  const nextLinkRef = React.useRef<string | undefined>(undefined);
  const [isIdDisabled, setIsIdDisabled] = React.useState(false);
  const [isNameDisabled, setIsNameDisabled] = React.useState(false);

  React.useEffect(() => {
    if (isUrlStateProcessed) {
      fetchData();
    }
  }, [isUrlStateProcessed]);

  React.useEffect(() => {
    if (kind) {
      setSelectedKind(kind.toString());
    }
    if (name) {
      setNameInput(name.toString());
    }
    if (subject) {
      setSubjectInput(subject.toString());
    }
    if (audit) {
      const auditName = audit.toString();
      setAuditContextState(getDefaultKindSelectState({ value: auditName, link: `/org/${org}/auditctx/${auditName}` }));
    }
    if (id) {
      setIdInput(id.toString());
    }

    // Initialize from/to from URL params
    if (urlFrom) {
      setFromNew(urlFrom);
    } else {
      setFromNew(getDefaultAuditTrailTimeNew());
    }
    if (urlTo) {
      setToNew(urlTo);
    }

    // Initialize timezone from URL if present
    if (urlTimezone) {
      const resolvedTimezone = urlValueToTimezone(urlTimezone, localTimezone);
      if (resolvedTimezone !== timezoneValue) {
        dispatch(setTimezoneValue(resolvedTimezone));
      }
    }

    setIsUrlStateProcessed(true);
  }, []);

  React.useEffect(() => {
    setIsIdDisabled(nameInput.length > 0);
    setIsNameDisabled(idInput.length > 0);
  }, [idInput, nameInput]);

  React.useEffect(() => {
    if (!queried || !isUrlStateProcessed) {
      return;
    }

    // Build base query object
    const baseQsObject: Record<string, unknown> = {};
    if (selectedKind) {
      baseQsObject.kind = selectedKind;
    }
    if (nameInput) {
      baseQsObject.name = nameInput;
    }
    if (subjectInput) {
      baseQsObject.subject = subjectInput;
    }
    if (auditContextState.value) {
      baseQsObject.audit = auditContextState.value;
    }
    if (idInput) {
      baseQsObject.id = idInput;
    }

    // Build query object with date range and timezone
    const qsObject = buildDateRangeQueryObject(
      {
        from: fromNew,
        to: toNew,
        timezone: timezoneToUrlValue(timezoneValue, localTimezone),
      },
      baseQsObject,
      { omitTimezoneWhenDefault: "UTC" } // Only include tz param when not UTC
    );

    const queryString = qs.stringify(qsObject, { arrayFormat: "comma", encode: false });
    navigate({ search: `?${queryString}` });
  }, [selectedKind, nameInput, subjectInput, auditContextState.value, idInput, fromNew, toNew, timezoneValue, localTimezone]);

  function onScroll_Items(params: ScrollParams) {
    if (!nextLinkRef.current) return;
    const { scrollHeight, scrollTop, clientHeight } = params;
    const val = scrollHeight - (scrollTop + clientHeight);
    if (val < 1) {
      fetchData();
    }
  }

  function onQuery() {
    nextLinkRef.current = undefined;
    setAudits([]);
    fetchData();
  }

  async function fetchData() {
    try {
      setIsLoading(true);
      let url = `/audit/org/${org}`;
      if (nextLinkRef.current) {
        url = nextLinkRef.current;
      } else {
        let params: string[] = [];
        if (kind === "org") {
          url += `/resource/name/${org}`;
        } else {
          if (nameInput.length > 0) {
            url += `/resource/name/${nameInput}`;
          }
          if (idInput.length > 0) {
            url += `/resource/id/${idInput}`;
          }
        }
        if (selectedKind !== "any") {
          params.push(`resourceType=${selectedKind}`);
        }
        if (subjectInput.length > 0) {
          params.push(`subjectName=${resolveSubjectName(subjectInput, org)}`);
        }
        if (!!auditContextState.value) {
          params.push(`contextName=${auditContextState.value}`);
        }
        // Resolve from/to together to handle start/end cross-references
        const resolved = resolveExpressionPair(fromNew, toNew, timezoneValue);
        if (resolved.from) {
          params.push(`fromEvent=${resolved.from.toISOString()}`);
        }
        if (resolved.to) {
          params.push(`toEvent=${resolved.to.toISOString()}`);
        }
        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }
      }
      const { data } = await request({ service: "audit", url });
      if (nextLinkRef.current) {
        setAudits([...audits, ...data.items]);
      } else {
        setAudits(data.items);
      }
      const _nextLink = data.links.find((l: any) => l.rel === "next")?.href;
      if (data.items.length === 100) {
        nextLinkRef.current = _nextLink;
      } else {
        nextLinkRef.current = undefined;
      }
      setQueried(true);
    } catch (e) {
      const isUnathorized = e.response?.status === 403;
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      if (isUnathorized) errorMessage = "Unauthorized";
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  }

  function onClear() {
    setSelectedKind("any");
    setNameInput("");
    setSubjectInput("");
    setIdInput("");
    setAuditContextState(defaultAuditContextState);
    setFromNew(getDefaultAuditTrailTimeNew());
    setToNew(null);
  }

  async function downloadAudits(format: "json" | "yaml") {
    try {
      let blob = "";

      if (format === "json") {
        blob = JSON.stringify(audits, null, 2);
      } else if (format === "yaml") {
        blob = jsYaml.dump(audits, { indent: 2, noRefs: true });
      }

      autoDownloadWithElementA(blob, format);
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  function autoDownloadWithElementA(blobData: any, format: "json" | "yaml") {
    const file = new Blob([blobData], { type: `text/${format}` });
    const href = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.style.display = "none";
    a.classList.add("cpln-temp-a");
    a.download = `audits-from-${fromNew}-to${toNew}.${format}`;
    a.href = href;
    a.click();
  }

  return (
    <>
      <Layout key={org}>
        <div className="flex items-baseline gap-4">
          <div className="mb-2 text-2xl font-medium">Audit Trail</div>
          {kind === "org" ? (
            <Alert
              message={
                <div>
                  Query for kind org is restricted to current org <span className="font-semibold">{org}</span>
                </div>
              }
            />
          ) : null}
        </div>
        <div className="flex items-center mb-2">
          <Select
            className="mr-2"
            style={{ width: 150 }}
            placeholder={"Kind"}
            onChange={setSelectedKind}
            options={[
              { label: "Any Kind", value: "any" },
              { label: "Billing Account", value: "account" },
              { label: "Agent", value: "agent" },
              { label: "Audit Context", value: "auditctx" },
              { label: "Cloud Account", value: "cloudaccount" },
              { label: "Domain", value: "domain" },
              { label: "Group", value: "group" },
              { label: "GVC", value: "gvc" },
              { label: "Identity", value: "identity" },
              { label: "Image", value: "image" },
              { label: "IP Set", value: "ipset" },
              { label: "Kubernetes", value: "mk8s" },
              { label: "Location", value: "location" },
              { label: "Org (Current)", value: "org" }, // TODO restrict with current org
              { label: "Policy", value: "policy" },
              { label: "Quota", value: "quota" },
              { label: "Secret", value: "secret" },
              { label: "Service Account", value: "serviceaccount" },
              { label: "Task", value: "task" },
              { label: "User", value: "user" },
              { label: "Volume Set", value: "volumeset" },
              { label: "Workload", value: "workload" },
            ]}
            value={selectedKind}
          />
          <Input
            className="mr-2"
            style={{ width: 500 }}
            value={kind === "org" ? org : nameInput}
            placeholder={"Resource Name"}
            onChange={(e) => setNameInput(e.target.value)}
            disabled={kind === "org" || isNameDisabled}
          />
          <Input
            className="flex-grow basis-0 mr-2"
            value={subjectInput}
            placeholder={"Subject (email or service account name)"}
            onChange={(e) => setSubjectInput(e.target.value)}
          />
          <div className="flex items-center" style={{ width: 150 }}>
            <Button
              type={"primary"}
              style={{ width: isLoading || audits.length < 1 ? 150 : 100 }}
              onClick={onQuery}
              disabled={isLoading}
              loading={isLoading}
            >
              Query
            </Button>
            {isLoading || audits.length < 1 ? null : (
              <Dropdown
                trigger={["click"]}
                menu={{
                  onClick: ({ key }: any) => {
                    switch (key) {
                      case "export-json":
                        downloadAudits("json");
                        break;
                      case "export-yaml":
                        downloadAudits("yaml");
                        break;
                      default:
                        break;
                    }
                  },
                  items: [
                    { key: "export-json", label: "JSON" },
                    { key: "export-yaml", label: "YAML" },
                  ],
                }}
              >
                <Button style={{ width: 45, marginLeft: 5 }} type={"default"} icon={<DownloadOutlined />} />
              </Dropdown>
            )}
          </div>
        </div>
        <div className="flex items-center mb-4">
          <KindSelect
            className="mr-2"
            optionValue="name"
            value={auditContextValue}
            onChange={setAuditContextValue}
            style={{ width: 150 }}
            kind={"auditctx"}
            placeholder="Any Context"
            onStateChange={(value) => setAuditContextState(value)}
          />
          <Input
            className="mr-2"
            style={{ width: 500 }}
            value={kind === "org" ? "" : idInput}
            placeholder={"Resource ID"}
            onChange={(e) => setIdInput(e.target.value)}
            disabled={kind === "org" || isIdDisabled}
          />
          <span className="mr-2">Time Range ({timezoneLabel})</span>
          <WithTimezone>
            <DateRangePicker
              presets={getAuditTrailPresetsNew()}
              allowClear={[false, true]}
              placeholder={["From", "To"]}
              granularity="second"
              from={fromNew}
              setFrom={setFromNew}
              to={toNew}
              setTo={setToNew}
            />
          </WithTimezone>
          <div className="flex-grow basis-0" />
          <Button type={"default"} style={{ width: 150 }} onClick={() => onClear()} disabled={isLoading}>
            Clear All Filters
          </Button>
        </div>
        {isLoading && !nextLinkRef.current && (
          <div className="flex flex-col items-center p-4 border" style={{ borderRadius: 6 }}>
            <Loader className="animate-spin mb-2" />
            <p className="text-center text-2xl mt-2">Loading Audit Events</p>
          </div>
        )}
        {audits.length < 1 && queried && !isLoading ? (
          <div className="flex flex-col items-center p-4 border">
            <Search className="mb-2" />
            <p className="text-center text-2xl mt-2">No Audit Events Found</p>
          </div>
        ) : null}
        <AuditGridWrapper
          audits={audits}
          emptySpaceValue={366}
          onDetail={(id) => setShowDetailsOf(id)}
          onScroll={onScroll_Items}
          queried={queried}
          diffSupport={false}
        />
      </Layout>
      {showDetailsOf ? (
        <ViewModal
          object={audits.find((a) => a.id === showDetailsOf)}
          title={"Audit Log"}
          onClose={() => setShowDetailsOf(null)}
          visible={!!showDetailsOf}
          filename={`audit-${audits.find((a) => a.id === showDetailsOf)?.id}`}
        />
      ) : null}
    </>
  );
};
